import { useCallback, useMemo, useRef } from 'react';
import { useHoverDirty } from 'react-use';
import { MarketplaceLogo } from 'src/components/common/MarketplaceLogo';
import {
  Content,
  getContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { ScrollableToolbar } from 'src/core/ui/ScrollableToolbar';
import { MarketplaceEventEditDialog } from 'src/dialogs/MarketplaceEventEditDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { EditIcon, IconsFill } from 'src/svgs/Viagogo';
import {
  compareMarketplace,
  getIsInternationalEvent,
} from 'src/utils/eventWithDataUtils';
import { getIsPlaceholderEvent } from 'src/utils/placeholderEventsUtils';
import {
  Event,
  Feature,
  Marketplace,
  Permission,
  SellerEventMarketplaceEventInfo,
  Venue,
} from 'src/WebApiController';

import { StatLabel } from '../../Listings/InventoryEventPage/InventoryEventDetail/components/Map/StatLabel';
import * as styles from './EventHeaderMarketplaceMetrics.css';
import {
  EventHeaderMetricsType,
  EventHeaderMetricsWithMarketplacesProps,
} from './EventHeaderMarketplaceMetrics.types';

export { EventHeaderMetricsType } from './EventHeaderMarketplaceMetrics.types';

export const EventHeaderMarketplacesWithMetrics = ({
  event,
  venue,
  metrics,
  marketplaceEventInfo,
  onRefetchMarketplaceEventInfo,
  ...stackProps
}: EventHeaderMetricsWithMarketplacesProps) => {
  const contentContext = useContentContext();

  const metricsContent = useMemo(() => {
    if (metrics.type === EventHeaderMetricsType.Summary) {
      return (
        <Stack gap="s">
          <div className={styles.summaryMetricsLabel}>
            <Content id={metrics.metrics.label} />
          </div>
          <div>{metrics.metrics.value}</div>
        </Stack>
      );
    }
    if (metrics.type === EventHeaderMetricsType.Stats) {
      return (
        <Stack>
          {metrics.stats.map((stat) => {
            if (stat.tooltip == null) {
              return (
                <StatLabel
                  key={stat.label}
                  title={getContent(stat.label, contentContext)}
                  stat={stat.value}
                  direction={stat.direction}
                />
              );
            }
            return (
              <EventHeaderMetricsWithHoverEffect
                key={stat.label}
                metrics={
                  <StatLabel
                    title={getContent(stat.label, contentContext)}
                    stat={stat.value}
                    direction={stat.direction}
                  />
                }
                tooltip={stat.tooltip}
              />
            );
          })}
        </Stack>
      );
    }
    return null;
  }, [contentContext, metrics]);

  return (
    <Stack direction="row" alignItems="center" {...stackProps}>
      {metricsContent}
      <EventMarketplaces
        event={event}
        venue={venue}
        marketplaceEventInfo={marketplaceEventInfo}
        onRefetchMarketplaceEventInfo={onRefetchMarketplaceEventInfo}
      />
    </Stack>
  );
};

const visibleMarketPlaces: Readonly<Marketplace[]> = [
  Marketplace.StubHub,
  Marketplace.VividSeats,
  Marketplace.Ticketmaster,
  Marketplace.SeatGeek,
];

const EventMarketplaces = ({
  event,
  venue,
  marketplaceEventInfo,
  onRefetchMarketplaceEventInfo,
}: {
  event: Event;
  venue?: Venue;
  marketplaceEventInfo?: SellerEventMarketplaceEventInfo[];
  onRefetchMarketplaceEventInfo?: () => void;
}) => {
  const isInternationalEvent = getIsInternationalEvent(venue?.ctryCode);

  const hasEventsEditPermission = useUserHasAnyOfPermissions(
    Permission.Events_Edit
  );

  const hasPlaceholderEventFeature = useUserHasFeature(
    Feature.PlaceholderEventGroup
  );
  // The backend sets `placeholder` only for synthesized placeholder events,
  // so its presence authoritatively identifies them.
  const isPlaceholderEvent =
    hasPlaceholderEventFeature && getIsPlaceholderEvent(event);

  const { launchDialog, closeDialog, dialogProps } = useBasicDialog();

  const onEventIdsEditClick = useCallback(() => {
    launchDialog();
  }, [launchDialog]);

  const marketPlaceLogos = useMemo(() => {
    {
      return marketplaceEventInfo
        ?.filter((marketplace) =>
          visibleMarketPlaces.includes(marketplace.marketplace)
        )
        .toSorted((a, b) => compareMarketplace(a.marketplace, b.marketplace))
        .map((minfo, index) => {
          const hasEventId =
            minfo.marketplace == Marketplace.StubHub ||
            Boolean(minfo?.eventIdOverride || minfo?.mappedEventId) ||
            (Marketplace.Ticketmaster && minfo.websiteEventId);

          if (
            minfo.marketplace !== Marketplace.StubHub &&
            isInternationalEvent
          ) {
            // We only show other marketplaces when the Event is US
            return null;
          }

          const onMkpClick =
            hasEventId || !hasEventsEditPermission
              ? undefined
              : onEventIdsEditClick;

          return (
            <MarketplaceLogo
              key={minfo.marketplace + index}
              marketplace={minfo.marketplace}
              event={event!}
              isViagogo={false}
              grayScaleColor={!hasEventId}
              marketplaceEventInfo={minfo}
              onClick={onMkpClick}
              logoDimensions={{ width: '20px', height: '20px' }}
              variant="square"
            />
          );
        });
    }
  }, [
    event,
    hasEventsEditPermission,
    isInternationalEvent,
    marketplaceEventInfo,
    onEventIdsEditClick,
  ]);

  if (isPlaceholderEvent) {
    return null;
  }

  return (
    <>
      <Stack direction="row" gap="m" justifyContent="end" alignItems="center">
        <ScrollableToolbar>
          <Stack
            direction="row"
            gap="s"
            justifyContent="end"
            style={{ flex: 1 }}
          >
            {marketPlaceLogos}
          </Stack>
        </ScrollableToolbar>
        <div style={{ display: 'flex', flexShrink: 0 }}>
          {hasEventsEditPermission && (
            <EditIcon
              withHoverEffect
              fill={IconsFill.textPrimary}
              onClick={onEventIdsEditClick}
              size={vars.iconSize.m}
            />
          )}
        </div>
      </Stack>

      <MarketplaceEventEditDialog
        {...dialogProps}
        event={event}
        mkpEventInfos={marketplaceEventInfo ?? []}
        onClosed={() => {
          onRefetchMarketplaceEventInfo?.();
          closeDialog();
        }}
      />
    </>
  );
};

export const EventHeaderMetricsWithHoverEffect = ({
  metrics,
  tooltip,
}: {
  metrics: React.ReactNode;
  tooltip: React.ReactNode;
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const isHovering = useHoverDirty(elementRef);
  const { isDarkMode } = useSiteTheme();

  return (
    <Tooltip
      open={isHovering}
      delayDuration={100}
      triggerContent={<div ref={elementRef}>{metrics}</div>}
      contentVariant={isDarkMode ? 'light' : 'dark'}
      align="center"
    >
      {tooltip}
    </Tooltip>
  );
};
